import { TFunction } from 'i18next';
import { Documentation } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import CategoryCell from '@lib/components/Table/commonComponents/CategoryCell/CategoryCell';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { CellProps, Column } from '@lib/components/Table/types';
import formatDurationCell from '../../utils/formatDurationCell';

export type AnalyticsDocumentRow = {
  id: string;
  documentationId: string;
  documentation?: Documentation;
  viewCount: number;
  uniqueViews: number;
  avgTime: number | null;
  minTime: number | null;
  maxTime: number | null;
};

type ColumnsArgs = {
  onOpen: (item: AnalyticsDocumentRow) => void;
  t: TFunction<'translation', undefined>;
};

export default function getTopDocumentsTableColumns({
  onOpen,
  t,
}: ColumnsArgs): Column<AnalyticsDocumentRow>[] {
  return [
    {
      id: 'name',
      Header: t('name'),
      Cell: ({ item }: CellProps<AnalyticsDocumentRow>) => (
        <DocumentationPreview
          attachment={item.documentation?.currentVersion?.attachmentAttached}
          customName={item.documentation?.name}
          onClick={() => onOpen(item)}
        />
      ),
      minWidth: 250,
      width: 280,
      sortable: true,
    },
    {
      id: 'categoryName',
      Header: t('category'),
      Cell: ({ item }: CellProps<AnalyticsDocumentRow>) => (
        <CategoryCell
          label={item.documentation?.category?.name}
          color={item.documentation?.category?.color}
        />
      ),
      minWidth: 120,
      maxWidth: 200,
      sortable: true,
    },
    {
      id: 'viewCount',
      Header: t('view-count'),
      accessorFn: ({ item }) => String(item.viewCount),
      Cell: (props: CellProps<AnalyticsDocumentRow>) => <TextCell {...props} />,
      minWidth: 110,
      width: 120,
      sortable: true,
    },
    {
      id: 'uniqueViews',
      Header: t('unique-views'),
      accessorFn: ({ item }) => String(item.uniqueViews),
      Cell: (props: CellProps<AnalyticsDocumentRow>) => <TextCell {...props} />,
      minWidth: 120,
      width: 130,
      sortable: true,
    },
    {
      id: 'avgTime',
      Header: t('avg-time'),
      headerTooltipInfo: {
        tooltipBody: t('avg-time-tooltip'),
        icon: 'info',
        symbolsOutlined: true,
      },
      accessorFn: ({ item }) => formatDurationCell(item.avgTime),
      Cell: (props: CellProps<AnalyticsDocumentRow>) => <TextCell {...props} />,
      minWidth: 110,
      width: 120,
      sortable: true,
    },
    {
      id: 'minTime',
      Header: t('min-time'),
      headerTooltipInfo: {
        tooltipBody: t('min-time-tooltip'),
        icon: 'info',
        symbolsOutlined: true,
      },
      accessorFn: ({ item }) => formatDurationCell(item.minTime),
      Cell: (props: CellProps<AnalyticsDocumentRow>) => <TextCell {...props} />,
      minWidth: 110,
      width: 120,
      sortable: true,
    },
    {
      id: 'maxTime',
      Header: t('max-time'),
      headerTooltipInfo: {
        tooltipBody: t('max-time-tooltip'),
        icon: 'info',
        symbolsOutlined: true,
      },
      accessorFn: ({ item }) => formatDurationCell(item.maxTime),
      Cell: (props: CellProps<AnalyticsDocumentRow>) => <TextCell {...props} />,
      minWidth: 110,
      width: 120,
      sortable: true,
    },
  ];
}
